import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Filter, Calendar, Star, Clock, ChevronLeft, ChevronRight,
  Play, Plus, TrendingUp, Users, Compass,
  UserPlus, Zap, Loader2, Clapperboard
} from 'lucide-react';
import CreatePost from '../components/CreatePost';
import PostCard from '../components/PostCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const COUNTRIES = [
  { code: 'IN', name: 'India' },
  { code: 'US', name: 'United States' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
];



// ═══════════════════════════════════════════════════════════════════════════════
// MOVIE CARD — preserved exactly from your original
// ═══════════════════════════════════════════════════════════════════════════════

function MovieCard({ item, activeTab }) {
  const title    = item.title || item.name;
  const dateStr  = item.release_date || item.first_air_date;
  const year     = dateStr ? dateStr.substring(0, 4) : 'TBA';
  const linkPath = `/${item.media_type === 'tv' ? 'tv' : 'movie'}/${item.id}`;

  const formatDate = (d) => {
    if (!d) return 'TBA';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <Link to={linkPath} className="group relative block" data-testid={`movie-card-${item.id}`}>
      <div className="bg-card rounded-xl overflow-hidden border border-white/5 transition-all duration-300 group-hover:border-yellow-500/40 group-hover:scale-[1.03] group-hover:shadow-2xl group-hover:shadow-black/60 h-full flex flex-col card-glow">
        <div className="relative aspect-[2/3] overflow-hidden bg-gray-900">
          {item.poster_path ? (
            <img
              src={`https://image.tmdb.org/t/p/w500${item.poster_path}`}
              alt={title}
              className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-600 text-xs">No Image</div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="absolute top-2 left-2 bg-black/65 backdrop-blur-md text-white text-xs font-bold px-2 py-1 rounded-lg flex items-center gap-1">
            {activeTab === 'upcoming'
              ? <><Clock size={10} className="text-blue-400" />{formatDate(dateStr)}</>
              : <><Star size={10} className="text-yellow-500 fill-yellow-500" />{item.vote_average?.toFixed(1)}</>
            }
          </div>
          {item.media_type === 'tv' && (
            <div className="absolute top-2 right-2 bg-green-500/80 backdrop-blur-md text-white text-[10px] font-bold px-2 py-0.5 rounded-lg">TV</div>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-2.5 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
            <div className="flex gap-2">
              <div className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-semibold">
                <Play className="w-3 h-3 fill-white" /> View
              </div>
              <div className="flex items-center justify-center px-3 py-1.5 rounded-lg bg-yellow-500/80 backdrop-blur-md text-black text-xs font-bold">
                <Plus className="w-3.5 h-3.5" />
              </div>
            </div>
          </div>
        </div>
        <div className="p-3 flex flex-col flex-grow">
          <h3 className="font-bold text-sm text-foreground line-clamp-1 group-hover:text-yellow-400 transition-colors">{title}</h3>
          <div className="flex items-center mt-1.5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Calendar size={11} />{year}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// SKELETON CARD — preserved exactly from your original
// ═══════════════════════════════════════════════════════════════════════════════

function SkeletonCard() {
  return (
    <div className="rounded-xl overflow-hidden border border-white/5 bg-card">
      <div className="aspect-[2/3] skeleton-shimmer bg-muted/20" />
      <div className="p-3 space-y-2">
        <div className="h-4 rounded-md skeleton-shimmer bg-muted/20 w-4/5" />
        <div className="h-3 rounded-md skeleton-shimmer bg-muted/20 w-2/5" />
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// FEED-SPECIFIC COMPONENTS (new)
// ═══════════════════════════════════════════════════════════════════════════════

function PostSkeleton() {
  return (
    <div className="bg-card border border-white/6 rounded-2xl p-5 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full skeleton-shimmer bg-muted/30" />
        <div className="space-y-2 flex-1">
          <div className="h-3.5 w-32 rounded-md skeleton-shimmer bg-muted/30" />
          <div className="h-3 w-20 rounded-md skeleton-shimmer bg-muted/30" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-3.5 w-full rounded-md skeleton-shimmer bg-muted/30" />
        <div className="h-3.5 w-4/5 rounded-md skeleton-shimmer bg-muted/30" />
        <div className="h-3.5 w-3/5 rounded-md skeleton-shimmer bg-muted/30" />
      </div>
      <div className="flex gap-3 pt-1">
        <div className="h-8 w-16 rounded-xl skeleton-shimmer bg-muted/20" />
        <div className="h-8 w-16 rounded-xl skeleton-shimmer bg-muted/20" />
      </div>
    </div>
  );
}

function EmptyFeed() {
  return (
    <div className="text-center py-16 border-2 border-dashed border-white/8 rounded-2xl">
      <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
      <h3 className="font-bold text-lg text-white mb-2">Your feed is quiet</h3>
      <p className="text-muted-foreground text-sm mb-6 max-w-xs mx-auto">
        Follow people with similar movie taste to see their posts here.
      </p>
      <Link
        to="/discover"
        className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-full font-bold text-sm hover:bg-primary/90 transition-all btn-glow"
      >
        <Compass className="w-4 h-4" /> Discover People
      </Link>
    </div>
  );
}

function SuggestedUserRow({ user, currentUser }) {
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading]         = useState(false);

  const handleFollow = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isFollowing) {
        await axios.delete(`${BACKEND_URL}/api/follow/${user.user_id}`, { withCredentials: true });
        setIsFollowing(false);
      } else {
        await axios.post(`${BACKEND_URL}/api/follow`, { target_user_id: user.user_id }, { withCredentials: true });
        setIsFollowing(true);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  return (
    <div className="flex items-center gap-3 group">
      <Link to={`/profile/${user.user_id}`} className="flex-shrink-0">
        <img src={user.picture} alt={user.name}
          className="w-9 h-9 rounded-full border border-white/10 group-hover:border-primary/40 transition-colors" />
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/profile/${user.user_id}`}>
          <p className="text-sm font-semibold text-white hover:text-primary transition-colors line-clamp-1">{user.name}</p>
        </Link>
        {user.shared_count > 0 && (
          <p className="text-[11px] text-muted-foreground">{user.shared_count} shared movies</p>
        )}
      </div>
      {currentUser && currentUser.user_id !== user.user_id && (
        <button onClick={handleFollow} disabled={loading}
          className={`flex-shrink-0 p-1.5 rounded-full transition-all disabled:opacity-50 ${
            isFollowing ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-primary hover:bg-primary/10'
          }`}>
          <UserPlus className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function SuggestedSidebar({ currentUser }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/discover/people?limit=4`, { withCredentials: true });
        setSuggestions(res.data.users || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (!currentUser || (!loading && suggestions.length === 0)) return null;

  return (
    <div className="bg-card border border-white/6 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-sm text-white">People You May Like</h3>
        <Link to="/discover" className="text-xs text-primary hover:underline font-semibold">See all</Link>
      </div>
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full skeleton-shimmer bg-muted/30 flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-24 rounded skeleton-shimmer bg-muted/30" />
                <div className="h-2.5 w-16 rounded skeleton-shimmer bg-muted/30" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {suggestions.map(user => (
            <SuggestedUserRow key={user.user_id} user={user} currentUser={currentUser} />
          ))}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// SOCIAL FEED TAB (new)
// ═══════════════════════════════════════════════════════════════════════════════

function SocialFeed({ currentUser }) {
  const [posts, setPosts]             = useState([]);
  const [feedType, setFeedType]       = useState('trending');
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage]               = useState(1);
  const [hasMore, setHasMore]         = useState(false);
  const [activeView, setActiveView]   = useState('for-you');

  const fetchPosts = useCallback(async (pageNum = 1, replace = true) => {
    if (pageNum === 1) setLoading(true); else setLoadingMore(true);
    try {
      const endpoint = activeView === 'following'
        ? `${BACKEND_URL}/api/posts/feed?page=${pageNum}`
        : `${BACKEND_URL}/api/posts/trending?page=${pageNum}`;
      const res  = await axios.get(endpoint, { withCredentials: true });
      const data = res.data;
      setPosts(prev => replace ? (data.posts || []) : [...prev, ...(data.posts || [])]);
      setFeedType(data.feed_type || 'trending');
      setHasMore(data.has_more || false);
      setPage(pageNum);
    } catch (err) {
      console.error('Feed error', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeView]);

  useEffect(() => { fetchPosts(1, true); }, [fetchPosts, activeView]);

  const handlePostCreated = (newPost) => setPosts(prev => [newPost, ...prev]);
  const handlePostDeleted = (postId)  => setPosts(prev => prev.filter(p => p.post_id !== postId));

  return (
    <div className="flex gap-8 items-start">

      {/* ── FEED COLUMN ─────────────────────────────────── */}
      <div className="flex-1 min-w-0 space-y-4">

        {/* Sub-tabs */}
        <div className="flex items-center justify-between">
          <div className="flex bg-white/5 border border-white/8 rounded-full p-1 gap-0.5">
            {[
              { key: 'for-you',   label: 'For You',  Icon: TrendingUp },
              { key: 'following', label: 'Following', Icon: Users },
            ].map(({ key, label, Icon }) => (
              <button key={key} onClick={() => setActiveView(key)}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold transition-all ${
                  activeView === key
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}>
                <Icon className="w-3.5 h-3.5" />{label}
              </button>
            ))}
          </div>
          {feedType === 'trending' && activeView === 'following' && (
            <p className="text-xs text-muted-foreground hidden sm:block">
              Showing trending — follow people to personalise
            </p>
          )}
        </div>

        {/* Composer */}
        {currentUser && (
          <CreatePost currentUser={currentUser} onPostCreated={handlePostCreated} />
        )}

        {/* Logged-out CTA */}
        {!currentUser && (
          <div className="flex items-center gap-3 bg-primary/8 border border-primary/20 rounded-2xl px-5 py-4">
            <TrendingUp className="w-5 h-5 text-primary flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-white">Trending across the platform</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                <Link to="/login" className="text-primary hover:underline font-semibold">Sign in</Link>{' '}
                to see posts from people with your movie taste.
              </p>
            </div>
          </div>
        )}

        {/* Posts */}
        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => <PostSkeleton key={i} />)}
          </div>
        ) : posts.length === 0 ? (
          <EmptyFeed />
        ) : (
          <div className="space-y-4 stagger-children">
            {posts.map(post => (
              <PostCard
                key={post.post_id}
                post={post}
                currentUser={currentUser}
                onDelete={handlePostDeleted}
              />
            ))}
          </div>
        )}

        {/* Load more */}
        {hasMore && !loading && (
          <div className="flex justify-center pt-2 pb-6">
            <button onClick={() => fetchPosts(page + 1, false)} disabled={loadingMore}
              className="flex items-center gap-2 px-6 py-2.5 bg-white/5 border border-white/10 rounded-full text-sm font-semibold text-gray-300 hover:bg-white/10 hover:text-white transition-all disabled:opacity-50">
              {loadingMore
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</>
                : 'Load more'
              }
            </button>
          </div>
        )}
      </div>

      {/* ── SIDEBAR ─────────────────────────────────────── */}
      <div className="w-72 flex-shrink-0 hidden lg:block space-y-4 sticky top-24">
        <SuggestedSidebar currentUser={currentUser} />
        <Link to="/discover"
          className="flex items-center gap-3 bg-gradient-to-r from-primary/15 to-transparent border border-primary/20 rounded-2xl px-5 py-4 hover:border-primary/40 transition-all group">
          <Compass className="w-5 h-5 text-primary flex-shrink-0 group-hover:rotate-12 transition-transform" />
          <div>
            <p className="text-sm font-bold text-white">Discover People</p>
            <p className="text-xs text-muted-foreground">Find your movie twin</p>
          </div>
        </Link>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// MOVIE DISCOVERY TAB — all original logic, zero changes
// ═══════════════════════════════════════════════════════════════════════════════

function MovieDiscovery() {
  const [activeTab, setActiveTab]             = useState('movie');
  const [content, setContent]                 = useState([]);
  const [genres, setGenres]                   = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [showFilters, setShowFilters]         = useState(false);
  const [selectedGenre, setSelectedGenre]     = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [currentPage, setCurrentPage]         = useState(1);
  const [totalPages, setTotalPages]           = useState(1);

  const heroItem     = content[0];
  const heroImage    = heroItem?.backdrop_path
    ? `https://image.tmdb.org/t/p/original${heroItem.backdrop_path}`
    : 'https://images.pexels.com/photos/7991226/pexels-photo-7991226.jpeg';
  const heroTitle    = heroItem?.title || heroItem?.name || '';
  const heroOverview = heroItem?.overview || '';

  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const type = activeTab === 'tv' ? 'tv' : 'movie';
        const res  = await axios.get(`${BACKEND_URL}/api/genres?media_type=${type}`);
        setGenres(res.data.genres || []);
      } catch (err) {
        console.error('Failed to fetch genres', err);
      }
    };
    fetchGenres();
  }, [activeTab]);

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      try {
        const type  = activeTab === 'tv' ? 'tv' : 'movie';
        const isUpc = activeTab === 'upcoming';
        let sort;
        if (isUpc)              sort = 'primary_release_date.asc';
        else if (type === 'tv') sort = 'first_air_date.desc';
        else                    sort = 'primary_release_date.desc';

        const params = { media_type: type, is_upcoming: isUpc, page: currentPage, sort_by: sort };
        if (selectedGenre)   params.with_genres = selectedGenre;
        if (selectedCountry) params.region      = selectedCountry;

        const res = await axios.get(`${BACKEND_URL}/api/discover`, { params });
        setContent(res.data.results || []);
        setTotalPages(Math.min(res.data.total_pages || 1, 500));
      } catch (error) {
        console.error('Failed to fetch content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [activeTab, selectedGenre, selectedCountry, currentPage]);

  const handleTabChange    = (tab)    => { setActiveTab(tab); setCurrentPage(1); };
  const handleFilterChange = (setter) => (e) => { setter(e.target.value); setCurrentPage(1); };
  const handlePageChange   = (newPage) => { setCurrentPage(newPage); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <div>
      {/* Hero — identical to your original */}
      <div className="relative min-h-[55vh] flex items-end justify-start mb-0 overflow-hidden">
        <div
          className="absolute inset-0 transition-all duration-1000 scale-105"
          style={{ backgroundImage: `url(${heroImage})`, backgroundSize: 'cover', backgroundPosition: 'center top' }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/75 to-background/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/70 via-transparent to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
          <div className="max-w-2xl space-y-4">
            {heroTitle && (
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-400 flex items-center gap-2">
                <span className="w-6 h-px bg-yellow-400" />
                {activeTab === 'upcoming' ? 'Coming Soon' : activeTab === 'tv' ? 'Trending Series' : 'Trending Film'}
              </p>
            )}
            <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tight text-white drop-shadow-xl leading-tight">
              {heroTitle || (activeTab === 'upcoming' ? 'Coming Soon' : 'Discover & Discuss')}
            </h1>
            {!heroTitle && (
              <p className="text-lg text-gray-300 drop-shadow-md">
                {activeTab === 'upcoming'
                  ? 'Check out what is releasing next'
                  : 'Your community for movie and TV conversations'}
              </p>
            )}
            {heroTitle && heroOverview && (
              <p className="text-gray-300 text-sm leading-relaxed line-clamp-2 max-w-lg">{heroOverview}</p>
            )}
            {heroItem && (
              <Link
                to={`/${heroItem.media_type === 'tv' ? 'tv' : 'movie'}/${heroItem.id}`}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-bold text-sm bg-yellow-500 text-black hover:bg-yellow-400 transition-all active:scale-95 shadow-lg shadow-yellow-500/25 btn-glow"
              >
                <Play className="w-4 h-4 fill-black" /> View Details
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Controls — identical to your original */}
      <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-6 pt-10">
        <div className="flex bg-white/5 border border-white/8 rounded-full p-1 gap-0.5">
          {[
            { key: 'movie',    label: 'Movies',   activeClass: 'bg-yellow-500 text-black' },
            { key: 'tv',       label: 'TV Shows',  activeClass: 'bg-green-500 text-black'  },
            { key: 'upcoming', label: 'Upcoming',  activeClass: 'bg-blue-500 text-white'   },
          ].map(({ key, label, activeClass }) => (
            <button key={key} onClick={() => handleTabChange(key)}
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all duration-200 ${
                activeTab === key ? `${activeClass} shadow-lg` : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {(selectedGenre || selectedCountry) && (
            <span className="text-xs text-yellow-400 font-semibold bg-yellow-400/10 border border-yellow-400/20 px-2.5 py-1 rounded-full">
              Filters active
            </span>
          )}
          <button onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
              showFilters ? 'bg-white text-black border-white' : 'border-white/15 text-gray-300 hover:border-white/40 hover:text-white'
            }`}>
            <Filter size={15} />
            <span className="text-sm font-semibold">Filters</span>
          </button>
        </div>
      </div>

      {/* Filter panel — identical to your original */}
      {showFilters && (
        <div className="glass-card p-6 rounded-2xl mb-10 animate-in fade-in slide-in-from-top-3 duration-200">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex flex-col gap-2">
              <label className="text-xs text-gray-400 uppercase font-bold tracking-wider">Genre</label>
              <select value={selectedGenre} onChange={handleFilterChange(setSelectedGenre)}
                className="bg-white/5 text-white px-4 py-2.5 rounded-lg border border-white/10 focus:outline-none focus:border-yellow-500/60 min-w-[160px] transition-colors">
                <option value="">All Genres</option>
                {genres.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs text-gray-400 uppercase font-bold tracking-wider">Country</label>
              <select value={selectedCountry} onChange={handleFilterChange(setSelectedCountry)}
                className="bg-white/5 text-white px-4 py-2.5 rounded-lg border border-white/10 focus:outline-none focus:border-yellow-500/60 min-w-[140px] transition-colors">
                <option value="">All Countries</option>
                {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </div>
            {(selectedGenre || selectedCountry) && (
              <button onClick={() => { setSelectedGenre(''); setSelectedCountry(''); setCurrentPage(1); }}
                className="px-4 py-2.5 text-red-400 text-sm font-bold hover:bg-red-500/10 rounded-lg transition-colors">
                Clear filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Grid — identical to your original */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
          {Array.from({ length: 15 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : content.length === 0 ? (
        <div className="text-center py-20 border-2 border-dashed border-white/8 rounded-2xl">
          <p className="text-gray-500 text-lg">No results found for these filters.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 stagger-children">
            {content.map(item => <MovieCard key={item.id} item={item} activeTab={activeTab} />)}
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-16 mb-4 gap-4">
              <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
                className="flex items-center gap-2 px-5 py-2.5 bg-white/4 border border-white/10 rounded-xl disabled:opacity-30 hover:bg-white/8 transition-colors font-bold text-sm">
                <ChevronLeft size={17} /> Previous
              </button>
              <span className="text-gray-400 font-medium bg-black/30 px-5 py-2.5 rounded-xl border border-white/8 text-sm tabular-nums">
                {currentPage} / {totalPages}
              </span>
              <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}
                className="flex items-center gap-2 px-5 py-2.5 bg-white/4 border border-white/10 rounded-xl disabled:opacity-30 hover:bg-white/8 transition-colors font-bold text-sm">
                Next <ChevronRight size={17} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// HOME — TOP-LEVEL PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export default function Home() {
  const [mainTab, setMainTab]         = useState('feed');
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then(res => setCurrentUser(res.data))
      .catch(() => setCurrentUser(null));
  }, []);

  return (
    <div className="min-h-screen pt-20" data-testid="home-page">


      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-6">

        {/* ── TOP-LEVEL TABS ────────────────────────────── */}
        <div className="flex items-center gap-1 mb-8 border-b border-white/8 pb-0">
          {[
            { key: 'feed',     label: 'Feed',        Icon: Zap          },
            { key: 'discover', label: 'Movies & TV',  Icon: Clapperboard },
          ].map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setMainTab(key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-bold transition-all border-b-2 -mb-px ${
                mainTab === key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-400 hover:text-white hover:border-white/20'
              }`}
            >
              <Icon className="w-4 h-4" />{label}
            </button>
          ))}
        </div>

        {/* ── TAB CONTENT ───────────────────────────────── */}
        {mainTab === 'feed'     && <SocialFeed currentUser={currentUser} />}
        {mainTab === 'discover' && <MovieDiscovery />}

      </div>
    </div>
  );
}
